import { NavLink, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../store/authStore';
import { useSettingsStore } from '../../store/settingsStore';
import { useT } from '../../hooks/useT';
import logo from '../../assets/logo140.png';

export default function Sidebar() {
  const { user, logout, isPaid } = useAuthStore();
  const { theme, toggleTheme, toggleLanguage, language } = useSettingsStore();
  const navigate = useNavigate();
  const t = useT();

  const navItems = [
    { to: '/dashboard',    labelKey: 'nav.dashboard', icon: '◈' },
    { to: '/goals',        labelKey: 'nav.goals',     icon: '◎' },
    { to: '/debt',         labelKey: 'nav.debt',      icon: '💳' },
    { to: '/transactions', labelKey: 'nav.transactions', icon: '📊' },
    { to: '/profile',      labelKey: 'nav.profile',   icon: '◉' },
    { to: '/settings',     labelKey: 'nav.settings',  icon: '⚙' },
  ];

  function handleLogout() {
    logout();
    navigate('/login');
  }

  return (
    <aside className="w-64 bg-surface-card border-r border-surface-border flex flex-col h-screen sticky top-0">
      {/* Logo */}
      <div className="p-6 border-b border-surface-border">
        <div className="flex items-center gap-3">
          <img src={logo} alt="Plannizzi AI" className="h-10 w-auto" />
          <div className="flex flex-col leading-tight">
            <span className="font-bold text-slate-100 text-base">Plannizzi AI</span>
            <span className="text-xs text-slate-500">{t('brand.slogan')}</span>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
        {navItems.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-colors duration-150 ${
                isActive
                  ? 'bg-brand-600/20 text-brand-400'
                  : 'text-slate-400 hover:text-slate-200 hover:bg-surface-hover'
              }`
            }
          >
            <span className="text-lg">{item.icon}</span>
            {t(item.labelKey)}
          </NavLink>
        ))}
      </nav>

      {/* Bottom controls */}
      <div className="p-4 border-t border-surface-border space-y-3">
        {/* Theme + Language */}
        <div className="flex gap-2">
          <button
            onClick={toggleTheme}
            title={t(`theme.toggle.${theme}`)}
            className="flex-1 text-xs text-slate-400 hover:text-slate-200 hover:bg-surface-hover px-2 py-1.5 rounded-lg transition-colors text-center truncate"
          >
            {t(`theme.toggle.${theme}`)}
          </button>
          <button
            onClick={toggleLanguage}
            title={t(`lang.switch.${language}`)}
            className="flex-1 text-xs text-slate-400 hover:text-slate-200 hover:bg-surface-hover px-2 py-1.5 rounded-lg transition-colors text-center truncate"
          >
            {t(`lang.switch.${language}`)}
          </button>
        </div>

        {/* Upgrade */}
        {!isPaid() && (
          <NavLink
            to="/pricing"
            className="block w-full text-center bg-brand-600/10 hover:bg-brand-600/20 border border-brand-700 text-brand-400 text-sm font-medium py-2 rounded-xl transition-colors"
          >
            {t('sidebar.upgrade')}
          </NavLink>
        )}

        {/* User info */}
        <div className="flex items-center gap-3 px-2">
          <div className="w-8 h-8 bg-surface-hover rounded-full flex items-center justify-center text-slate-400 font-semibold text-sm flex-shrink-0">
            {user?.full_name?.[0]?.toUpperCase() || 'U'}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-slate-200 truncate">{user?.full_name}</p>
            <p className="text-xs text-slate-500 truncate">{user?.email}</p>
          </div>
          <button onClick={handleLogout} className="text-slate-500 hover:text-slate-300 transition-colors flex-shrink-0" title={t('sidebar.logout')}>
            ⇤
          </button>
        </div>
      </div>
    </aside>
  );
}
